import { ActionIcon, Group, Stack, Table, Text, TextInput, Checkbox, NumberInput } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { IconX } from '@tabler/icons-react';
import { DECIMAL_INPUT_PROPS } from '../../components/form/numberFieldPresets';
import { statusPalette } from '../../theme/colors';
import type { FuelAshFormValues, FuelAshRowState, FuelAshSection } from './fuelAshForm';
import { PRIMARY_SOURCE_HINTS } from './fuelAshForm';
import classes from './FuelAshCategoriesPanel.module.css';

interface FuelAshCategoryTableProps {
  form: UseFormReturnType<FuelAshFormValues>;
  section: FuelAshSection;
  disabled: boolean;
  /** `key` der zuletzt angelegten Zeile — bekommt den Fokus im Bezeichnungsfeld (D-12). */
  focusKey: string | null;
  /** Nur bei `section="secondary"` belegt (D-19: Primärzeilen tragen keinen Löschknopf). */
  onDeleteRow?: (index: number) => void;
}

/** Kleiner, gedimmter Hinweis-Chip — neutrale Zusatzinformation an einer Zeile (Hinweis auf den
 *  Kundendatei-Namen, „kein Startwert"). Kein `@mantine/core`-`Badge`: dessen Standardformen
 *  (pill, uppercase) passen nicht zum Sketch-Tag-Look; ein einfacher `Text`-Chip reicht. */
function NeutralTag({ children }: { children: string }) {
  return (
    <Text
      component="span"
      size="xs"
      c="vdzGray.6"
      px={6}
      py={1}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: 4,
        backgroundColor: 'var(--mantine-color-vdzGray-1)',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Text>
  );
}

/** Auffangkategorie-Kennzeichen — gleiche Farbgebung wie der CSI-Chip in `PlantDetail.tsx`
 *  (`statusPalette.inPruefung`). */
function CatchAllTag() {
  return (
    <Text
      component="span"
      fw={700}
      fz={12}
      px={7}
      py={1}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: 999,
        backgroundColor: statusPalette.inPruefung.bg,
        color: statusPalette.inPruefung.fg,
        border: `1px solid ${statusPalette.inPruefung.border}`,
        whiteSpace: 'nowrap',
      }}
    >
      Auffangkategorie
    </Text>
  );
}

/**
 * Tabelle je Abschnitt (Primär/Sekundär) der Aschegehalt-Einstellungsseite (Sketch 018 Variante B,
 * D-19). Verdrahtet ausschließlich über `form.getInputProps` — kein eigener Änderungs-Handler,
 * kein Zurücksetzen-Knopf (D-08), keine vom Haken abhängige Feldsperre (D-02), keine Sortierung im
 * Formular (D-12: die Reihenfolge kommt vom Server bzw. bleibt beim Anlegen an Ort und Stelle).
 */
export function FuelAshCategoryTable({ form, section, disabled, focusKey, onDeleteRow }: FuelAshCategoryTableProps) {
  const rows = form.getValues()[section];
  const isSecondary = section === 'secondary';

  return (
    <Table.ScrollContainer minWidth={820}>
      <Table layout="fixed" data-testid={`fuel-ash-table-${section}`}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{isSecondary ? 'Kategorie' : 'Brennstoff'}</Table.Th>
            <Table.Th ta="right" className={classes.colNumber}>
              Startwert
            </Table.Th>
            <Table.Th ta="right" className={classes.colNumber}>
              Min
            </Table.Th>
            <Table.Th ta="right" className={classes.colNumber}>
              Max
            </Table.Th>
            <Table.Th ta="center" className={classes.colBinding}>
              Grenzen bindend
            </Table.Th>
            <Table.Th className={classes.colDelete} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row, index) => (
            <Table.Tr key={row.key} data-row-key={row.key}>
              <Table.Td>{renderNameCell(row, section, index, form, disabled, focusKey)}</Table.Td>
              <Table.Td ta="right">
                <NumberInput
                  size="xs"
                  className={classes.numberInput}
                  aria-label={`${row.name || 'Neue Kategorie'} Startwert`}
                  rightSection={
                    <Text size="xs" c="dimmed">
                      %
                    </Text>
                  }
                  rightSectionPointerEvents="none"
                  {...DECIMAL_INPUT_PROPS}
                  fixedDecimalScale
                  {...form.getInputProps(`${section}.${index}.startValue`)}
                  disabled={disabled}
                />
              </Table.Td>
              <Table.Td ta="right">
                <NumberInput
                  size="xs"
                  className={classes.numberInput}
                  aria-label={`${row.name || 'Neue Kategorie'} Min`}
                  rightSection={
                    <Text size="xs" c="dimmed">
                      %
                    </Text>
                  }
                  rightSectionPointerEvents="none"
                  {...DECIMAL_INPUT_PROPS}
                  fixedDecimalScale
                  {...form.getInputProps(`${section}.${index}.minValue`)}
                  disabled={disabled}
                />
              </Table.Td>
              <Table.Td ta="right">
                <NumberInput
                  size="xs"
                  className={classes.numberInput}
                  aria-label={`${row.name || 'Neue Kategorie'} Max`}
                  rightSection={
                    <Text size="xs" c="dimmed">
                      %
                    </Text>
                  }
                  rightSectionPointerEvents="none"
                  {...DECIMAL_INPUT_PROPS}
                  fixedDecimalScale
                  {...form.getInputProps(`${section}.${index}.maxValue`)}
                  disabled={disabled}
                />
              </Table.Td>
              <Table.Td ta="center">
                <Checkbox
                  aria-label={`${row.name || 'Neue Kategorie'} Grenzen bindend`}
                  {...form.getInputProps(`${section}.${index}.binding`, { type: 'checkbox' })}
                  disabled={disabled}
                />
              </Table.Td>
              {isSecondary ? (
                <Table.Td ta="right">
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    aria-label={`Kategorie ${row.name || 'neue Kategorie'} löschen`}
                    title={
                      row.catchAll
                        ? 'Die Auffangkategorie kann nicht entfernt werden'
                        : 'Kategorie entfernen'
                    }
                    disabled={disabled || row.catchAll}
                    onClick={() => onDeleteRow?.(index)}
                  >
                    <IconX size={16} />
                  </ActionIcon>
                </Table.Td>
              ) : (
                <Table.Td />
              )}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

function renderNameCell(
  row: FuelAshRowState,
  section: FuelAshSection,
  index: number,
  form: UseFormReturnType<FuelAshFormValues>,
  disabled: boolean,
  focusKey: string | null
) {
  const missingStartValue = row.startValue === '';

  if (section === 'primary') {
    const hint = row.code ? PRIMARY_SOURCE_HINTS[row.code] : undefined;
    return (
      <Group gap={6} wrap="nowrap">
        <Text size="sm" c="vdzGray.9">
          {row.name}
        </Text>
        <Text size="xs" c="dimmed">
          fest
        </Text>
        {hint && <NeutralTag>{hint}</NeutralTag>}
        {missingStartValue && <NeutralTag>kein Startwert</NeutralTag>}
      </Group>
    );
  }

  const showMissingTag = row.id !== undefined && missingStartValue;

  return (
    <Stack gap={2}>
      <Group gap={6} wrap="wrap">
        <TextInput
          size="xs"
          className={classes.nameInput}
          aria-label="Bezeichnung der Kategorie"
          placeholder="Bezeichnung der Kategorie"
          autoFocus={row.key === focusKey}
          {...form.getInputProps(`secondary.${index}.name`)}
          disabled={disabled || row.nameLocked}
        />
        {row.catchAll && <CatchAllTag />}
        {showMissingTag && <NeutralTag>kein Startwert</NeutralTag>}
      </Group>
      {row.nameLocked && (
        <Text size="xs" c="dimmed">
          Bezeichnung nicht mehr änderbar, da in Meldungen verwendet
        </Text>
      )}
    </Stack>
  );
}
